// @ts-nocheck
import React, { useEffect, useState, useRef } from 'react';
import fetchWithAuth from '../../utils/fetchWithAuth.ts';
import { auth } from '../../firebase.js';
import RecipeCardWide from '../Shared/RecipeCards/RecipeCardWide.tsx';
import { useSwipeable } from 'react-swipeable';
import MealEntryServingEditor from './MealEntryServingEditor.tsx';
import { Input } from '../ui/atoms/input/Input';
import BrandSpinner from '../ui/atoms/BrandSpinner/BrandSpinner.tsx';
import type { Recipe } from '../../types';

interface InlineRecipePickerProps {
  dayIndex: number;
  weekStart: Date;
  onClose: () => void;
  onAdded: () => Promise<void>;
  tab: number; // 0=Yours,1=Saved,2=Search
  setTab: React.Dispatch<React.SetStateAction<number>>;
}

const InlineRecipePicker: React.FC<InlineRecipePickerProps> = ({
  dayIndex,
  weekStart,
  onClose,
  onAdded,
  tab,
  setTab,
}) => {
  const profileId = auth.currentUser?.uid;

  const [createdRecipes, setCreatedRecipes] = useState<Recipe[]>([]);
  const [savedRecipes, setSavedRecipes] = useState<Recipe[]>([]);
  const [searchResults, setSearchResults] = useState<Recipe[]>([]);

  const [loadingCreated, setLoadingCreated] = useState(false);
  const [loadingSaved, setLoadingSaved] = useState(false);
  const [loadingSearch, setLoadingSearch] = useState(false);

  const [overlayRecipeId, setOverlayRecipeId] = useState<string | null>(null);
  const [tempServings, setTempServings] = useState(1);
  const overlayRef = useRef<HTMLDivElement>(null);

  const [searchTerm, setSearchTerm] = useState('');

  const tabLabels = ['Yours', 'Saved', 'Search'];

  // helper to flip bookmark flag
  const flipBookmarkInArray = (arr: Recipe[], recipeId: string) =>
    arr.map((r) =>
      r.id === recipeId ? { ...r, isBookmarked: !r.isBookmarked } : r
    );

  // toggle bookmark in ALL lists so UI updates immediately
  const handleToggleBookmark = async (recipe: Recipe) => {
    if (!profileId) return;
    try {
      const payload = { recipeId: recipe.id };
      if (recipe.isBookmarked) {
        await fetchWithAuth(`/api/bookmarks`, {
          method: 'DELETE',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
      } else {
        await fetchWithAuth(`/api/bookmarks`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
      }

      // update all three arrays
      setCreatedRecipes((prev) => flipBookmarkInArray(prev, recipe.id));
      setSavedRecipes((prev) => flipBookmarkInArray(prev, recipe.id));
      setSearchResults((prev) => flipBookmarkInArray(prev, recipe.id));
    } catch (err) {
      console.error('Failed to toggle bookmark in InlineRecipePicker:', err);
    }
  };

  // fetch for Yours / Saved tabs
  useEffect(() => {
    if (!profileId) return;
    if (tab === 0) {
      setLoadingCreated(true);
      fetchWithAuth(`/api/users/${profileId}/recipes`)
        .then(setCreatedRecipes)
        .catch(console.error)
        .finally(() => setLoadingCreated(false));
    } else if (tab === 1) {
      setLoadingSaved(true);
      fetchWithAuth(`/api/bookmarks/user/${profileId}`)
        .then(setSavedRecipes)
        .catch(console.error)
        .finally(() => setLoadingSaved(false));
    }
  }, [tab, profileId]);

  // fetch for Search tab
  useEffect(() => {
    if (tab !== 2) return;
    setLoadingSearch(true);
    const q = encodeURIComponent(searchTerm);
    fetchWithAuth(`/api/recipes/search?query=${q}&page=1&limit=20`)
      .then((res) => setSearchResults(res.recipes))
      .catch(console.error)
      .finally(() => setLoadingSearch(false));
  }, [searchTerm, tab]);

  // close overlay on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        overlayRecipeId &&
        overlayRef.current &&
        !overlayRef.current.contains(e.target as Node)
      ) {
        setOverlayRecipeId(null);
        setTempServings(1);
      }
    };
    if (overlayRecipeId) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [overlayRecipeId]);

  // add to meal plan
  const handleConfirmAdd = async (recipeId: string, servings: number) => {
    try {
      await fetchWithAuth(`/api/meal-plan/entry`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          weekStart: weekStart.toISOString(),
          day: dayIndex,
          recipeId,
          servings,
        }),
      });
      await onAdded();
      onClose();
    } catch (err) {
      console.error('Failed to add recipe to meal plan', err);
    }
  };

  // swipe between tabs
  const swipeHandlers = useSwipeable({
    onSwipedLeft: () =>
      setTab((prev) => Math.min(prev + 1, tabLabels.length - 1)),
    onSwipedRight: () => setTab((prev) => Math.max(prev - 1, 0)),
    delta: 40,
    trackTouch: true,
    trackMouse: false,
  });

  const recipesToShow = tab === 0 ? createdRecipes : savedRecipes;
  const loading =
    tab === 0 ? loadingCreated : tab === 1 ? loadingSaved : loadingSearch;

  return (
    <div className="mt-1" {...swipeHandlers}>
      {/* Tabs */}
      <div className="flex items-center justify-between border-b mb-3 px-1">
        <div className="flex">
          {tabLabels.map((label, idx) => (
            <button
              key={idx}
              onClick={() => setTab(idx)}
              className={`py-2 px-3 text-sm font-medium ${
                tab === idx
                  ? 'border-b-2 border-primary text-primary'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      {tab === 2 ? (
        <div className="space-y-3 px-1">
          {/* Search bar */}
          <div className="flex items-center gap-2">
            <Input
              type="text"
              className="flex-1 rounded px-3 py-2"
              placeholder="Search by recipe title or ingredients"
              value={searchTerm}
              setValue={setSearchTerm}
            />
          </div>

          {/* Spinner / Empty / Results */}
          {loading ? (
            <div className="py-6 flex justify-center">
              <BrandSpinner />
            </div>
          ) : searchResults.length === 0 ? (
            <div className="text-center text-gray-500 py-6">
              No recipes found.
            </div>
          ) : (
            searchResults.map((recipe) => (
              <div key={recipe.id} className="relative">
                <RecipeCardWide
                  recipe={recipe}
                  isBookmarked={recipe.isBookmarked}
                  onToggleBookmark={() => handleToggleBookmark(recipe)}
                  onAddToPlan={
                    overlayRecipeId === recipe.id
                      ? undefined
                      : () => setOverlayRecipeId(recipe.id)
                  }
                  hideAddButton={overlayRecipeId === recipe.id}
                />

                {overlayRecipeId === recipe.id && (
                  <div
                    ref={overlayRef}
                    className="absolute inset-0 z-50 bg-white/75 backdrop-blur-sm flex items-center justify-center rounded shadow"
                  >
                    <div className="w-full px-4">
                      <MealEntryServingEditor
                        servings={tempServings}
                        setServings={setTempServings}
                        onSave={async () => {
                          await handleConfirmAdd(recipe.id, tempServings);
                          setOverlayRecipeId(null);
                          setTempServings(1);
                        }}
                      />
                    </div>
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      ) : (
        <div className="space-y-3 px-1">
          {/* Spinner / Empty / Listed Recipes for Yours & Saved */}
          {loading ? (
            <div className="py-6 flex justify-center">
              <BrandSpinner />
            </div>
          ) : recipesToShow.length === 0 ? (
            <div className="text-center text-gray-500 py-6">
              {tab === 0 ? (
                <>
                  <p className="text-sm text-gray-600">
                    You haven’t created any recipes yet.
                  </p>
                  <p className="mb-2 text-sm text-gray-600">
                    Create your own recipes and they will appear here.
                  </p>
                </>
              ) : (
                <>
                  <p className="text-sm text-gray-600">
                    You haven’t saved any recipes yet.
                  </p>
                  <p className="mb-2 text-sm text-gray-600">
                    Bookmark recipes to see them in this tab.
                  </p>
                </>
              )}
            </div>
          ) : (
            recipesToShow.map((recipe) => (
              <div key={recipe.id} className="relative">
                <RecipeCardWide
                  recipe={recipe}
                  isBookmarked={recipe.isBookmarked}
                  onToggleBookmark={() => handleToggleBookmark(recipe)}
                  onAddToPlan={
                    overlayRecipeId === recipe.id
                      ? undefined
                      : () => setOverlayRecipeId(recipe.id)
                  }
                />

                {overlayRecipeId === recipe.id && (
                  <div
                    ref={overlayRef}
                    className="absolute inset-0 z-50 bg-white/75 backdrop-blur-sm flex items-center justify-center rounded shadow"
                  >
                    <div className="w-full px-4">
                      <MealEntryServingEditor
                        servings={tempServings}
                        setServings={setTempServings}
                        onSave={async () => {
                          await handleConfirmAdd(recipe.id, tempServings);
                          setOverlayRecipeId(null);
                          setTempServings(1);
                        }}
                      />
                    </div>
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default InlineRecipePicker;
